import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider from './context/AuthContext';
import Layout from './components/common/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/Loader';
import AuthProtectedRoute from './pages/Auth/AuthProtectedRoute';
import ProtectedRoute from './pages/Auth/ProtectedRoute';
import DepositScreen from './components/Dashboard/Deposit/DepositScreen';
import DashboardScreen from './components/Dashboard/DashboardScreen';
import TradingPlatforms from './components/Dashboard/platforms';
import TransferScreen from './components/Dashboard/Transfer';
import ReportScreen from './components/Dashboard/Report';
import WithdrawalScreen from './components/Dashboard/withdrawal/Withdrawal';
import ReferralScreen from './components/Dashboard/Refer';
import DemoAccountForm from './components/DemoAccountForm';
import SuccessMessage from './components/SuccessMessage';
import ForgotPassword from './components/Auth/Forget-Password';
import ResetPassword from './components/Auth/Reset-Password';
import AccountCreationScreen from './components/Dashboard/AccuntCreatinScreen';
import WalletCreationScreen from './components/Dashboard/WaletCreationScreen';
import MyDocuments from './components/Dashboard/MyDocuments';
import Settings from './components/Dashboard/Settings';
import LegalDocuments from './components/Dashboard/LegalDocuments';
import Profile from './components/Dashboard/Profile';
import EditProfile from './components/Dashboard/EditProfile';
import SupportWidget from './components/SupportWidget';
import SignupForm from './components/Auth/SignupForm';
import VerificationComplete from './components/Onboarding/VerificationComplete';
// Lazy load components
const Signup = lazy(() => import('./pages/Auth/Signup'));
const Login = lazy(() => import('./pages/Auth/Login'));
const EmailVerification = lazy(() => import('./components/Onboarding/EmailVerification'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AccountNatureSelectionScreen = lazy(() => import('./components/Onboarding/AccountNatureSelectionScreen'));

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Layout><Signup /></Layout>} />
              <Route path="/login" element={<Layout><Login /></Layout>} />
              <Route path="/signup" element={<Layout><Signup /></Layout>} />
              <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
              <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
              <Route path="/email-verification" element={
                <Layout>
                  <AuthProtectedRoute>
                    <EmailVerification />
                  </AuthProtectedRoute>
                </Layout>
              } />
              {/* Marketing-friendly aliases (cross-subdomain deep links) */}
              <Route path="/deposit" element={<Navigate to="/dashboard/deposit" replace />} />
              <Route path="/withdraw" element={<Navigate to="/dashboard/withdrawal" replace />} />
              <Route path="/transfer" element={<Navigate to="/dashboard/transfer" replace />} />
              <Route path="/account/settings" element={<Navigate to="/dashboard/settings" replace />} />

              <Route path="/demo-account" element={<Layout><DemoAccountForm /></Layout>} />
              <Route path="/success" element={<Layout><SuccessMessage /></Layout>} />
              <Route path="/verification-complete" element={
                <Layout>
                  <AuthProtectedRoute>
                    <VerificationComplete />
                  </AuthProtectedRoute>
                </Layout>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }>
                <Route index element={<DashboardScreen />} /> {/* Default nested route */}
                <Route path="deposit" element={<DepositScreen />} />
                <Route path="withdrawal" element={<WithdrawalScreen />} />
                <Route path="transfer" element={<TransferScreen />} />
                <Route path="reports" element={<ReportScreen />} />
                <Route path="platforms" element={<TradingPlatforms />} />
                <Route path="refer-friends" element={<ReferralScreen />} />
                <Route path="my-documents" element={<MyDocuments />} />
                <Route path="legal-documents" element={<LegalDocuments />} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                <Route path="edit-profile" element={<EditProfile />} />
                <Route path="create-account/:type" element={<AccountCreationScreen />} />
                <Route path="create-wallet" element={<WalletCreationScreen />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
